import { Controller } from "@hotwired/stimulus";
var BackendRequest_default = class {
	promise;
	actions;
	updatedModels;
	isResolved = false;
	constructor(promise, actions, updateModels) {
		this.promise = promise;
		this.promise.then((response) => {
			this.isResolved = true;
			return response;
		});
		this.actions = actions;
		this.updatedModels = updateModels;
	}
	containsOneOfActions(targetedActions) {
		return this.actions.filter((action) => targetedActions.includes(action)).length > 0;
	}
	areAnyModelsUpdated(targetedModels) {
		return this.updatedModels.filter((model) => targetedModels.includes(model)).length > 0;
	}
};
var RequestBuilder_default = class {
	url;
	method;
	credentials;
	constructor(url, method = "post", credentials = "same-origin") {
		this.url = url;
		this.method = method;
		this.credentials = credentials;
	}
	buildRequest(props, actions, updated, children, updatedPropsFromParent, files) {
		const splitUrl = this.url.split("?");
		let [url] = splitUrl;
		const [, queryString] = splitUrl;
		const params = new URLSearchParams(queryString || "");
		const fetchOptions = {};
		fetchOptions.credentials = this.credentials;
		fetchOptions.headers = {
			Accept: "application/vnd.live-component+html",
			"X-Requested-With": "XMLHttpRequest",
			"X-Live-Url": window.location.pathname + window.location.search
		};
		const totalFiles = Object.entries(files).reduce((total, current) => total + current.length, 0);
		const hasFingerprints = Object.keys(children).length > 0;
		if (actions.length === 0 && totalFiles === 0 && this.method === "get" && this.willDataFitInUrl(JSON.stringify(props), JSON.stringify(updated), params, JSON.stringify(children), JSON.stringify(updatedPropsFromParent))) {
			params.set("props", JSON.stringify(props));
			params.set("updated", JSON.stringify(updated));
			if (Object.keys(updatedPropsFromParent).length > 0) params.set("propsFromParent", JSON.stringify(updatedPropsFromParent));
			if (hasFingerprints) params.set("children", JSON.stringify(children));
			fetchOptions.method = "GET";
		} else {
			fetchOptions.method = "POST";
			const requestData = {
				props,
				updated
			};
			if (Object.keys(updatedPropsFromParent).length > 0) requestData.propsFromParent = updatedPropsFromParent;
			if (hasFingerprints) requestData.children = children;
			if (actions.length > 0) {
				if (actions.length === 1) {
					requestData.args = actions[0].args;
					url += `/${encodeURIComponent(actions[0].name)}`;
				} else {
					url += "/_batch";
					requestData.actions = actions;
				}
			}
			const formData = new FormData();
			formData.append("data", JSON.stringify(requestData));
			for (const [key, value] of Object.entries(files)) {
				const length = value.length;
				for (let i = 0; i < length; ++i) formData.append(key, value[i]);
			}
			fetchOptions.body = formData;
		}
		const paramsString = params.toString();
		return {
			url: `${url}${paramsString.length > 0 ? `?${paramsString}` : ""}`,
			fetchOptions
		};
	}
	willDataFitInUrl(propsJson, updatedJson, params, childrenJson, propsFromParentJson) {
		return (new URLSearchParams(propsJson + updatedJson + childrenJson + propsFromParentJson).toString() + params.toString()).length < 1500;
	}
};
var Backend_default = class {
	requestBuilder;
	constructor(url, method = "post", credentials = "same-origin") {
		this.requestBuilder = new RequestBuilder_default(url, method, credentials);
	}
	makeRequest(props, actions, updated, children, updatedPropsFromParent, files) {
		const { url, fetchOptions } = this.requestBuilder.buildRequest(props, actions, updated, children, updatedPropsFromParent, files);
		return new BackendRequest_default(fetch(url, fetchOptions), actions.map((backendAction) => backendAction.name), Object.keys(updated));
	}
};
var BackendResponse_default = class {
	response;
	body;
	liveUrl;
	download = null;
	parsePromise = null;
	constructor(response) {
		this.response = response;
	}
	async getBody() {
		if (!this.parsePromise) this.parsePromise = this.parse();
		await this.parsePromise;
		return this.body;
	}
	getDownload() {
		return this.download;
	}
	getLiveUrl() {
		if (void 0 === this.liveUrl) this.liveUrl = this.response.headers.get("X-Live-Url");
		return this.liveUrl;
	}
	getDownloadUrl() {
		return this.response.headers.get("X-Live-Download-Url");
	}
	isRemoved() {
		return this.response.headers.has("X-Live-Remove");
	}
	async parse() {
		const htmlLength = this.response.headers.get("X-Live-Html-Length");
		if (null === htmlLength) {
			this.body = await this.response.text();
			return;
		}
		const buffer = await this.response.arrayBuffer();
		const splitAt = Number.parseInt(htmlLength, 10);
		this.body = new TextDecoder().decode(buffer.slice(0, splitAt));
		this.download = {
			filename: decodeFilename(this.response.headers.get("X-Live-Download-Filename")),
			blob: new Blob([buffer.slice(splitAt)], { type: this.response.headers.get("X-Live-Download-Type") ?? "application/octet-stream" })
		};
	}
};
function decodeFilename(value) {
	if (!value) return "download";
	try {
		return decodeURIComponent(value) || "download";
	} catch {
		return "download";
	}
}
function getElementAsTagText(element) {
	return element.innerHTML ? element.outerHTML.slice(0, element.outerHTML.indexOf(element.innerHTML)) : element.outerHTML;
}
let componentMapByElement = /* @__PURE__ */ new WeakMap();
let componentMapByComponent = /* @__PURE__ */ new Map();
const registerComponent = (component) => {
	componentMapByElement.set(component.element, component);
	componentMapByComponent.set(component, component.name);
};
const unregisterComponent = (component) => {
	componentMapByElement.delete(component.element);
	componentMapByComponent.delete(component);
};
const getComponent = (element) => new Promise((resolve, reject) => {
	let count = 0;
	const maxCount = 10;
	const interval = setInterval(() => {
		const component = componentMapByElement.get(element);
		if (component) {
			clearInterval(interval);
			resolve(component);
		}
		count++;
		if (count > maxCount) {
			clearInterval(interval);
			reject(/* @__PURE__ */ new Error(`Component not found for element ${getElementAsTagText(element)}`));
		}
	}, 5);
});
const findComponents = (currentComponent, onlyParents, onlyMatchName) => {
	const components = [];
	componentMapByComponent.forEach((componentName, component) => {
		if (onlyParents && (currentComponent === component || !component.element.contains(currentComponent.element))) return;
		if (onlyMatchName && componentName !== onlyMatchName) return;
		components.push(component);
	});
	return components;
};
const findChildren = (currentComponent) => {
	const children = [];
	componentMapByComponent.forEach((componentName, component) => {
		if (currentComponent === component) return;
		if (!currentComponent.element.contains(component.element)) return;
		let foundChildComponent = false;
		componentMapByComponent.forEach((childComponentName, childComponent) => {
			if (foundChildComponent) return;
			if (childComponent === component) return;
			if (childComponent.element.contains(component.element)) foundChildComponent = true;
		});
		children.push(component);
	});
	return children;
};
const findParent = (currentComponent) => {
	let parentElement = currentComponent.element.parentElement;
	while (parentElement) {
		const component = componentMapByElement.get(parentElement);
		if (component) return component;
		parentElement = parentElement.parentElement;
	}
	return null;
};
function parseDirectives(content) {
	const directives = [];
	if (!content) return directives;
	let currentActionName = "";
	let currentArgumentValue = "";
	let currentArguments = [];
	let currentModifiers = [];
	let state = "action";
	const getLastActionName = () => {
		if (currentActionName) return currentActionName;
		if (directives.length === 0) throw new Error("Could not find any directives");
		return directives[directives.length - 1].action;
	};
	const pushInstruction = () => {
		directives.push({
			action: currentActionName,
			args: currentArguments,
			modifiers: currentModifiers,
			getString: () => {
				return content;
			}
		});
		currentActionName = "";
		currentArgumentValue = "";
		currentArguments = [];
		currentModifiers = [];
		state = "action";
	};
	const pushArgument = () => {
		currentArguments.push(currentArgumentValue.trim());
		currentArgumentValue = "";
	};
	const pushModifier = () => {
		if (currentArguments.length > 1) throw new Error(`The modifier "${currentActionName}()" does not support multiple arguments.`);
		currentModifiers.push({
			name: currentActionName,
			value: currentArguments.length > 0 ? currentArguments[0] : null
		});
		currentActionName = "";
		currentArguments = [];
		state = "action";
	};
	for (let i = 0; i < content.length; i++) {
		const char = content[i];
		switch (state) {
			case "action":
				if (char === "(") {
					state = "arguments";
					break;
				}
				if (char === " ") {
					if (currentActionName) pushInstruction();
					break;
				}
				if (char === "|") {
					pushModifier();
					break;
				}
				currentActionName += char;
				break;
			case "arguments":
				if (char === ")") {
					pushArgument();
					state = "after_arguments";
					break;
				}
				if (char === ",") {
					pushArgument();
					break;
				}
				currentArgumentValue += char;
				break;
			case "after_arguments":
				if (char === "|") {
					pushModifier();
					break;
				}
				if (char !== " ") throw new Error(`Missing space after ${getLastActionName()}()`);
				pushInstruction();
		}
	}
	switch (state) {
		case "action":
		case "after_arguments":
			if (currentActionName) pushInstruction();
			break;
		default: throw new Error(`Did you forget to add a closing ")" after "${currentActionName}"?`);
	}
	return directives;
}
function combineSpacedArray(parts) {
	const finalParts = [];
	parts.forEach((part) => {
		finalParts.push(...trimAll(part).split(" "));
	});
	return finalParts;
}
function trimAll(str) {
	return str.replace(/[\s]+/g, " ").trim();
}
function normalizeModelName(model) {
	if (!model.includes("[") && !model.includes("]")) return model;
	return model.replace(/\[]$/, "").split("[").map((s) => s.replace("]", "")).join(".");
}
function getValueFromElement(element, valueStore) {
	if (element instanceof HTMLInputElement) {
		if (element.type === "checkbox") {
			const modelNameData = getModelDirectiveFromElement(element, false);
			if (modelNameData !== null) {
				const modelValue = valueStore.get(modelNameData.action);
				if (Array.isArray(modelValue)) return getMultipleCheckboxValue(element, modelValue);
				if (Object(modelValue) === modelValue) return getMultipleCheckboxValue(element, Object.values(modelValue));
			}
			if (element.hasAttribute("value")) return element.checked ? element.getAttribute("value") : null;
			return element.checked;
		}
		return inputValue(element);
	}
	if (element instanceof HTMLSelectElement) {
		if (element.multiple) return Array.from(element.selectedOptions).map((el) => el.value);
		return element.value;
	}
	if (element.hasAttribute("data-value")) return element.dataset.value;
	if ("value" in element) return element.value;
	if (element.hasAttribute("value")) return element.getAttribute("value");
	return null;
}
function setValueOnElement(element, value) {
	if (element instanceof HTMLInputElement) {
		if (element.type === "file") return;
		if (element.type === "radio") {
			element.checked = element.value == value;
			return;
		}
		if (element.type === "checkbox") {
			if (Array.isArray(value)) element.checked = value.some((val) => val == element.value);
			else if (element.hasAttribute("value")) element.checked = element.value == value;
			else element.checked = value;
			return;
		}
	}
	if (element instanceof HTMLSelectElement) {
		const arrayWrappedValue = [].concat(value).map((value) => {
			return `${value}`;
		});
		Array.from(element.options).forEach((option) => {
			option.selected = arrayWrappedValue.includes(option.value);
		});
		return;
	}
	value = value === void 0 ? "" : value;
	element.value = value;
}
function getAllModelDirectiveFromElements(element) {
	if (!element.dataset.model) return [];
	const directives = parseDirectives(element.dataset.model);
	directives.forEach((directive) => {
		if (directive.args.length > 0) throw new Error(`The data-model="${element.dataset.model}" format is invalid: it does not support passing arguments to the model.`);
		directive.action = normalizeModelName(directive.action);
	});
	return directives;
}
function getModelDirectiveFromElement(element, throwOnMissing = true) {
	const dataModelDirectives = getAllModelDirectiveFromElements(element);
	if (dataModelDirectives.length > 0) return dataModelDirectives[0];
	if (element.getAttribute("name")) {
		const formElement = element.closest("form");
		if (formElement && "model" in formElement.dataset) {
			const directive = parseDirectives(formElement.dataset.model || "*")[0];
			if (directive.args.length > 0) throw new Error(`The data-model="${formElement.dataset.model}" format is invalid: it does not support passing arguments to the model.`);
			directive.action = normalizeModelName(element.getAttribute("name"));
			return directive;
		}
	}
	if (!throwOnMissing) return null;
	throw new Error(`Cannot determine the model name for "${getElementAsTagText(element)}": the element must either have a "data-model" (or "name" attribute living inside a <form data-model="*">).`);
}
function elementBelongsToThisComponent(element, component) {
	if (component.element === element) return true;
	if (!component.element.contains(element)) return false;
	return element.closest("[data-controller~=\"live\"]") === component.element;
}
function cloneHTMLElement(element) {
	const newElement = element.cloneNode(true);
	if (!(newElement instanceof HTMLElement)) throw new Error("Could not clone element");
	return newElement;
}
function htmlToElement(html) {
	const template = document.createElement("template");
	html = html.trim();
	template.innerHTML = html;
	if (template.content.childElementCount > 1) throw new Error(`Component HTML contains ${template.content.childElementCount} elements, but only 1 root element is allowed.`);
	const child = template.content.firstElementChild;
	if (!child) throw new Error("Child not found");
	if (!(child instanceof HTMLElement)) throw new Error(`Created element is not an HTMLElement: ${html.trim()}`);
	return child;
}
const getMultipleCheckboxValue = (element, currentValues) => {
	const finalValues = [...currentValues];
	const value = inputValue(element);
	const index = currentValues.indexOf(value);
	if (element.checked) {
		if (index === -1) finalValues.push(value);
		return finalValues;
	}
	if (index > -1) finalValues.splice(index, 1);
	return finalValues;
};
const inputValue = (element) => element.dataset.value ? element.dataset.value : element.value;
function isTextualInputElement(el) {
	return el instanceof HTMLInputElement && [
		"text",
		"email",
		"password",
		"search",
		"tel",
		"url"
	].includes(el.type);
}
function isTextareaElement(el) {
	return el instanceof HTMLTextAreaElement;
}
function isNumericalInputElement(element) {
	return element instanceof HTMLInputElement && ["number", "range"].includes(element.type);
}
var HookManager_default = class {
	hooks = /* @__PURE__ */ new Map();
	register(hookName, callback) {
		const hooks = this.hooks.get(hookName) || [];
		hooks.push(callback);
		this.hooks.set(hookName, hooks);
	}
	unregister(hookName, callback) {
		const hooks = this.hooks.get(hookName) || [];
		const index = hooks.indexOf(callback);
		if (index === -1) return;
		hooks.splice(index, 1);
		this.hooks.set(hookName, hooks);
	}
	triggerHook(hookName, ...args) {
		(this.hooks.get(hookName) || []).forEach((callback) => {
			callback(...args);
		});
	}
};
var Idiomorph = (function() {
	"use strict";
	const noOp = () => {};
	const defaults = {
		morphStyle: "outerHTML",
		callbacks: {
			beforeNodeAdded: noOp,
			afterNodeAdded: noOp,
			beforeNodeMorphed: noOp,
			afterNodeMorphed: noOp,
			beforeNodeRemoved: noOp,
			afterNodeRemoved: noOp,
			beforeAttributeUpdated: noOp
		},
		head: {
			style: "merge",
			shouldPreserve: (elt) => elt.getAttribute("im-preserve") === "true",
			shouldReAppend: (elt) => elt.getAttribute("im-re-append") === "true",
			shouldRemove: noOp,
			afterHeadMorphed: noOp
		},
		restoreFocus: true
	};
	function morph(oldNode, newContent, config = {}) {
		oldNode = normalizeElement(oldNode);
		const newNode = normalizeParent(newContent);
		const ctx = createMorphContext(oldNode, newNode, config);
		const morphedNodes = saveAndRestoreFocus(ctx, () => {
			return withHeadBlocking(ctx, oldNode, newNode, (ctx) => {
				if (ctx.morphStyle === "innerHTML") {
					morphChildren(ctx, oldNode, newNode);
					return Array.from(oldNode.childNodes);
				} else return morphOuterHTML(ctx, oldNode, newNode);
			});
		});
		ctx.pantry.remove();
		return morphedNodes;
	}
	function morphOuterHTML(ctx, oldNode, newNode) {
		const oldParent = normalizeParent(oldNode);
		morphChildren(ctx, oldParent, newNode, oldNode, oldNode.nextSibling);
		return Array.from(oldParent.childNodes);
	}
	function saveAndRestoreFocus(ctx, fn) {
		if (!ctx.config.restoreFocus) return fn();
		let activeElement = document.activeElement;
		if (!(activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement)) return fn();
		const { id: activeElementId, selectionStart, selectionEnd } = activeElement;
		const results = fn();
		if (activeElementId && activeElementId !== document.activeElement?.getAttribute("id")) {
			activeElement = ctx.target.querySelector(`[id="${activeElementId}"]`);
			activeElement?.focus();
		}
		if (activeElement && !activeElement.selectionEnd && selectionEnd) activeElement.setSelectionRange(selectionStart, selectionEnd);
		return results;
	}
	const morphChildren = (function() {
		function morphChildren(ctx, oldParent, newParent, insertionPoint = null, endPoint = null) {
			if (oldParent instanceof HTMLTemplateElement && newParent instanceof HTMLTemplateElement) {
				oldParent = oldParent.content;
				newParent = newParent.content;
			}
			insertionPoint ||= oldParent.firstChild;
			for (const newChild of newParent.childNodes) {
				if (insertionPoint && insertionPoint != endPoint) {
					const bestMatch = findBestMatch(ctx, newChild, insertionPoint, endPoint);
					if (bestMatch) {
						if (bestMatch !== insertionPoint) removeNodesBetween(ctx, insertionPoint, bestMatch);
						morphNode(bestMatch, newChild, ctx);
						insertionPoint = bestMatch.nextSibling;
						continue;
					}
				}
				if (newChild instanceof Element) {
					const newChildId = newChild.getAttribute("id");
					if (ctx.persistentIds.has(newChildId)) {
						const movedChild = moveBeforeById(oldParent, newChildId, insertionPoint, ctx);
						morphNode(movedChild, newChild, ctx);
						insertionPoint = movedChild.nextSibling;
						continue;
					}
				}
				const insertedNode = createNode(oldParent, newChild, insertionPoint, ctx);
				if (insertedNode) insertionPoint = insertedNode.nextSibling;
			}
			while (insertionPoint && insertionPoint != endPoint) {
				const tempNode = insertionPoint;
				insertionPoint = insertionPoint.nextSibling;
				removeNode(ctx, tempNode);
			}
		}
		function createNode(oldParent, newChild, insertionPoint, ctx) {
			if (ctx.callbacks.beforeNodeAdded(newChild) === false) return null;
			if (ctx.idMap.has(newChild)) {
				const newEmptyChild = document.createElement(newChild.tagName);
				oldParent.insertBefore(newEmptyChild, insertionPoint);
				morphNode(newEmptyChild, newChild, ctx);
				ctx.callbacks.afterNodeAdded(newEmptyChild);
				return newEmptyChild;
			} else {
				const newClonedChild = document.importNode(newChild, true);
				oldParent.insertBefore(newClonedChild, insertionPoint);
				ctx.callbacks.afterNodeAdded(newClonedChild);
				return newClonedChild;
			}
		}
		const findBestMatch = (function() {
			function findBestMatch(ctx, node, startPoint, endPoint) {
				let softMatch = null;
				let nextSibling = node.nextSibling;
				let siblingSoftMatchCount = 0;
				let cursor = startPoint;
				while (cursor && cursor != endPoint) {
					if (isSoftMatch(cursor, node)) {
						if (isIdSetMatch(ctx, cursor, node)) return cursor;
						if (softMatch === null) {
							if (!ctx.idMap.has(cursor)) softMatch = cursor;
						}
					}
					if (softMatch === null && nextSibling && isSoftMatch(cursor, nextSibling)) {
						siblingSoftMatchCount++;
						nextSibling = nextSibling.nextSibling;
						if (siblingSoftMatchCount >= 2) softMatch = void 0;
					}
					if (ctx.activeElementAndParents.includes(cursor)) break;
					cursor = cursor.nextSibling;
				}
				return softMatch || null;
			}
			function isIdSetMatch(ctx, oldNode, newNode) {
				let oldSet = ctx.idMap.get(oldNode);
				let newSet = ctx.idMap.get(newNode);
				if (!newSet || !oldSet) return false;
				for (const id of oldSet) if (newSet.has(id)) return true;
				return false;
			}
			function isSoftMatch(oldNode, newNode) {
				const oldElt = oldNode;
				const newElt = newNode;
				return oldElt.nodeType === newElt.nodeType && oldElt.tagName === newElt.tagName && (!oldElt.getAttribute?.("id") || oldElt.getAttribute?.("id") === newElt.getAttribute?.("id"));
			}
			return findBestMatch;
		})();
		function removeNode(ctx, node) {
			if (ctx.idMap.has(node)) moveBefore(ctx.pantry, node, null);
			else {
				if (ctx.callbacks.beforeNodeRemoved(node) === false) return;
				node.parentNode?.removeChild(node);
				ctx.callbacks.afterNodeRemoved(node);
			}
		}
		function removeNodesBetween(ctx, startInclusive, endExclusive) {
			let cursor = startInclusive;
			while (cursor && cursor !== endExclusive) {
				let tempNode = cursor;
				cursor = cursor.nextSibling;
				removeNode(ctx, tempNode);
			}
			return cursor;
		}
		function moveBeforeById(parentNode, id, after, ctx) {
			const target = ctx.target.getAttribute?.("id") === id && ctx.target || ctx.target.querySelector(`[id="${id}"]`) || ctx.pantry.querySelector(`[id="${id}"]`);
			removeElementFromAncestorsIdMaps(target, ctx);
			moveBefore(parentNode, target, after);
			return target;
		}
		function removeElementFromAncestorsIdMaps(element, ctx) {
			const id = element.getAttribute("id");
			while (element = element.parentNode) {
				let idSet = ctx.idMap.get(element);
				if (idSet) {
					idSet.delete(id);
					if (!idSet.size) ctx.idMap.delete(element);
				}
			}
		}
		function moveBefore(parentNode, element, after) {
			if (parentNode.moveBefore) try {
				parentNode.moveBefore(element, after);
			} catch (e) {
				parentNode.insertBefore(element, after);
			}
			else parentNode.insertBefore(element, after);
		}
		return morphChildren;
	})();
	const morphNode = (function() {
		function morphNode(oldNode, newContent, ctx) {
			if (ctx.ignoreActive && oldNode === document.activeElement) return null;
			if (ctx.callbacks.beforeNodeMorphed(oldNode, newContent) === false) return oldNode;
			if (oldNode instanceof HTMLHeadElement && ctx.head.ignore) {} else if (oldNode instanceof HTMLHeadElement && ctx.head.style !== "morph") handleHeadElement(oldNode, newContent, ctx);
			else {
				morphAttributes(oldNode, newContent, ctx);
				if (!ignoreValueOfActiveElement(oldNode, ctx)) morphChildren(ctx, oldNode, newContent);
			}
			ctx.callbacks.afterNodeMorphed(oldNode, newContent);
			return oldNode;
		}
		function morphAttributes(oldNode, newNode, ctx) {
			let type = newNode.nodeType;
			if (type === 1) {
				const oldElt = oldNode;
				const newElt = newNode;
				const oldAttributes = oldElt.attributes;
				const newAttributes = newElt.attributes;
				for (const newAttribute of newAttributes) {
					if (ignoreAttribute(newAttribute.name, oldElt, "update", ctx)) continue;
					if (oldElt.getAttribute(newAttribute.name) !== newAttribute.value) oldElt.setAttribute(newAttribute.name, newAttribute.value);
				}
				for (let i = oldAttributes.length - 1; 0 <= i; i--) {
					const oldAttribute = oldAttributes[i];
					if (!oldAttribute) continue;
					if (!newElt.hasAttribute(oldAttribute.name)) {
						if (ignoreAttribute(oldAttribute.name, oldElt, "remove", ctx)) continue;
						oldElt.removeAttribute(oldAttribute.name);
					}
				}
				if (!ignoreValueOfActiveElement(oldElt, ctx)) syncInputValue(oldElt, newElt, ctx);
			}
			if (type === 8 || type === 3) {
				if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
			}
		}
		function syncInputValue(oldElement, newElement, ctx) {
			if (oldElement instanceof HTMLInputElement && newElement instanceof HTMLInputElement && newElement.type !== "file") {
				let newValue = newElement.value;
				let oldValue = oldElement.value;
				syncBooleanAttribute(oldElement, newElement, "checked", ctx);
				syncBooleanAttribute(oldElement, newElement, "disabled", ctx);
				if (!newElement.hasAttribute("value")) {
					if (!ignoreAttribute("value", oldElement, "remove", ctx)) {
						oldElement.value = "";
						oldElement.removeAttribute("value");
					}
				} else if (oldValue !== newValue) {
					if (!ignoreAttribute("value", oldElement, "update", ctx)) {
						oldElement.setAttribute("value", newValue);
						oldElement.value = newValue;
					}
				}
			} else if (oldElement instanceof HTMLOptionElement && newElement instanceof HTMLOptionElement) syncBooleanAttribute(oldElement, newElement, "selected", ctx);
			else if (oldElement instanceof HTMLTextAreaElement && newElement instanceof HTMLTextAreaElement) {
				let newValue = newElement.value;
				let oldValue = oldElement.value;
				if (ignoreAttribute("value", oldElement, "update", ctx)) return;
				if (newValue !== oldValue) oldElement.value = newValue;
				if (oldElement.firstChild && oldElement.firstChild.nodeValue !== newValue) oldElement.firstChild.nodeValue = newValue;
			}
		}
		function syncBooleanAttribute(oldElement, newElement, attributeName, ctx) {
			const newLiveValue = newElement[attributeName];
			if (newLiveValue !== oldElement[attributeName]) {
				const ignoreUpdate = ignoreAttribute(attributeName, oldElement, "update", ctx);
				if (!ignoreUpdate) oldElement[attributeName] = newElement[attributeName];
				if (newLiveValue) {
					if (!ignoreUpdate) oldElement.setAttribute(attributeName, "");
				} else if (!ignoreAttribute(attributeName, oldElement, "remove", ctx)) oldElement.removeAttribute(attributeName);
			}
		}
		function ignoreAttribute(attr, element, updateType, ctx) {
			if (attr === "value" && ctx.ignoreActiveValue && element === document.activeElement) return true;
			return ctx.callbacks.beforeAttributeUpdated(attr, element, updateType) === false;
		}
		function ignoreValueOfActiveElement(possibleActiveElement, ctx) {
			return !!ctx.ignoreActiveValue && possibleActiveElement === document.activeElement && possibleActiveElement !== document.body;
		}
		return morphNode;
	})();
	function withHeadBlocking(ctx, oldNode, newNode, callback) {
		if (ctx.head.block) {
			const oldHead = oldNode.querySelector("head");
			const newHead = newNode.querySelector("head");
			if (oldHead && newHead) {
				const promises = handleHeadElement(oldHead, newHead, ctx);
				return Promise.all(promises).then(() => {
					return callback(Object.assign(ctx, { head: {
						block: false,
						ignore: true
					} }));
				});
			}
		}
		return callback(ctx);
	}
	function handleHeadElement(oldHead, newHead, ctx) {
		let added = [];
		let removed = [];
		let preserved = [];
		let nodesToAppend = [];
		let srcToNewHeadNodes = /* @__PURE__ */ new Map();
		for (const newHeadChild of newHead.children) srcToNewHeadNodes.set(newHeadChild.outerHTML, newHeadChild);
		for (const currentHeadElt of oldHead.children) {
			let inNewContent = srcToNewHeadNodes.has(currentHeadElt.outerHTML);
			let isReAppended = ctx.head.shouldReAppend(currentHeadElt);
			let isPreserved = ctx.head.shouldPreserve(currentHeadElt);
			if (inNewContent || isPreserved) {
				if (isReAppended) removed.push(currentHeadElt);
				else {
					srcToNewHeadNodes.delete(currentHeadElt.outerHTML);
					preserved.push(currentHeadElt);
				}
			} else if (ctx.head.style === "append") {
				if (isReAppended) {
					removed.push(currentHeadElt);
					nodesToAppend.push(currentHeadElt);
				}
			} else if (ctx.head.shouldRemove(currentHeadElt) !== false) removed.push(currentHeadElt);
		}
		nodesToAppend.push(...srcToNewHeadNodes.values());
		let promises = [];
		for (const newNode of nodesToAppend) {
			let newElt = document.createRange().createContextualFragment(newNode.outerHTML).firstChild;
			if (ctx.callbacks.beforeNodeAdded(newElt) !== false) {
				if ("href" in newElt && newElt.href || "src" in newElt && newElt.src) {
					/** @type {(result?: any) => void} */ let resolve;
					let promise = new Promise(function(_resolve) {
						resolve = _resolve;
					});
					newElt.addEventListener("load", function() {
						resolve();
					});
					promises.push(promise);
				}
				oldHead.appendChild(newElt);
				ctx.callbacks.afterNodeAdded(newElt);
				added.push(newElt);
			}
		}
		for (const removedElement of removed) if (ctx.callbacks.beforeNodeRemoved(removedElement) !== false) {
			oldHead.removeChild(removedElement);
			ctx.callbacks.afterNodeRemoved(removedElement);
		}
		ctx.head.afterHeadMorphed(oldHead, {
			added,
			kept: preserved,
			removed
		});
		return promises;
	}
	const createMorphContext = (function() {
		function createMorphContext(oldNode, newContent, config) {
			const { persistentIds, idMap } = createIdMaps(oldNode, newContent);
			const mergedConfig = mergeDefaults(config);
			const morphStyle = mergedConfig.morphStyle || "outerHTML";
			if (!["innerHTML", "outerHTML"].includes(morphStyle)) throw `Do not understand how to morph style ${morphStyle}`;
			return {
				target: oldNode,
				newContent,
				config: mergedConfig,
				morphStyle,
				ignoreActive: mergedConfig.ignoreActive,
				ignoreActiveValue: mergedConfig.ignoreActiveValue,
				restoreFocus: mergedConfig.restoreFocus,
				idMap,
				persistentIds,
				pantry: createPantry(),
				activeElementAndParents: createActiveElementAndParents(oldNode),
				callbacks: mergedConfig.callbacks,
				head: mergedConfig.head
			};
		}
		function mergeDefaults(config) {
			let finalConfig = Object.assign({}, defaults);
			Object.assign(finalConfig, config);
			finalConfig.callbacks = Object.assign({}, defaults.callbacks, config.callbacks);
			finalConfig.head = Object.assign({}, defaults.head, config.head);
			return finalConfig;
		}
		function createPantry() {
			const pantry = document.createElement("div");
			pantry.hidden = true;
			document.body.insertAdjacentElement("afterend", pantry);
			return pantry;
		}
		function createActiveElementAndParents(oldNode) {
			let activeElementAndParents = [];
			let elt = document.activeElement;
			if (elt?.tagName !== "BODY" && oldNode.contains(elt)) while (elt) {
				activeElementAndParents.push(elt);
				if (elt === oldNode) break;
				elt = elt.parentElement;
			}
			return activeElementAndParents;
		}
		function findIdElements(root) {
			let elements = Array.from(root.querySelectorAll("[id]"));
			if (root.getAttribute?.("id")) elements.push(root);
			return elements;
		}
		function populateIdMapWithTree(idMap, persistentIds, root, elements) {
			for (const elt of elements) {
				const id = elt.getAttribute("id");
				if (persistentIds.has(id)) {
					let current = elt;
					while (current) {
						let idSet = idMap.get(current);
						if (idSet == null) {
							idSet = /* @__PURE__ */ new Set();
							idMap.set(current, idSet);
						}
						idSet.add(id);
						if (current === root) break;
						current = current.parentElement;
					}
				}
			}
		}
		function createIdMaps(oldContent, newContent) {
			const oldIdElements = findIdElements(oldContent);
			const newIdElements = findIdElements(newContent);
			const persistentIds = createPersistentIds(oldIdElements, newIdElements);
			let idMap = /* @__PURE__ */ new Map();
			populateIdMapWithTree(idMap, persistentIds, oldContent, oldIdElements);
			populateIdMapWithTree(idMap, persistentIds, newContent.__idiomorphRoot || newContent, newIdElements);
			return {
				persistentIds,
				idMap
			};
		}
		function createPersistentIds(oldIdElements, newIdElements) {
			let duplicateIds = /* @__PURE__ */ new Set();
			let oldIdTagNameMap = /* @__PURE__ */ new Map();
			for (const { id, tagName } of oldIdElements) if (oldIdTagNameMap.has(id)) duplicateIds.add(id);
			else oldIdTagNameMap.set(id, tagName);
			let persistentIds = /* @__PURE__ */ new Set();
			for (const { id, tagName } of newIdElements) if (persistentIds.has(id)) duplicateIds.add(id);
			else if (oldIdTagNameMap.get(id) === tagName) persistentIds.add(id);
			for (const id of duplicateIds) persistentIds.delete(id);
			return persistentIds;
		}
		return createMorphContext;
	})();
	const { normalizeElement, normalizeParent } = (function() {
		const generatedByIdiomorph = /* @__PURE__ */ new WeakSet();
		function normalizeElement(content) {
			if (content instanceof Document) return content.documentElement;
			else return content;
		}
		function normalizeParent(newContent) {
			if (newContent == null) return document.createElement("div");
			else if (typeof newContent === "string") return normalizeParent(parseContent(newContent));
			else if (generatedByIdiomorph.has(newContent)) return newContent;
			else if (newContent instanceof Node) {
				if (newContent.parentNode) return new SlicedParentNode(newContent);
				else {
					const dummyParent = document.createElement("div");
					dummyParent.append(newContent);
					return dummyParent;
				}
			} else {
				const dummyParent = document.createElement("div");
				for (const elt of [...newContent]) dummyParent.append(elt);
				return dummyParent;
			}
		}
		class SlicedParentNode {
			constructor(node) {
				this.originalNode = node;
				this.realParentNode = node.parentNode;
				this.previousSibling = node.previousSibling;
				this.nextSibling = node.nextSibling;
			}
			get childNodes() {
				const nodes = [];
				let cursor = this.previousSibling ? this.previousSibling.nextSibling : this.realParentNode.firstChild;
				while (cursor && cursor != this.nextSibling) {
					nodes.push(cursor);
					cursor = cursor.nextSibling;
				}
				return nodes;
			}
			querySelectorAll(selector) {
				return this.childNodes.reduce((results, node) => {
					if (node instanceof Element) {
						if (node.matches(selector)) results.push(node);
						const nodeList = node.querySelectorAll(selector);
						for (let i = 0; i < nodeList.length; i++) results.push(nodeList[i]);
					}
					return results;
				}, []);
			}
			insertBefore(node, referenceNode) {
				return this.realParentNode.insertBefore(node, referenceNode);
			}
			moveBefore(node, referenceNode) {
				return this.realParentNode.moveBefore(node, referenceNode);
			}
			get __idiomorphRoot() {
				return this.originalNode;
			}
		}
		function parseContent(newContent) {
			let parser = new DOMParser();
			let contentWithSvgsRemoved = newContent.replace(/<svg(\s[^>]*>|>)([\s\S]*?)<\/svg>/gim, "");
			if (contentWithSvgsRemoved.match(/<\/html>/) || contentWithSvgsRemoved.match(/<\/head>/) || contentWithSvgsRemoved.match(/<\/body>/)) {
				let content = parser.parseFromString(newContent, "text/html");
				if (contentWithSvgsRemoved.match(/<\/html>/)) {
					generatedByIdiomorph.add(content);
					return content;
				} else {
					let htmlElement = content.firstChild;
					if (htmlElement) generatedByIdiomorph.add(htmlElement);
					return htmlElement;
				}
			} else {
				let content = parser.parseFromString("<body><template>" + newContent + "</template></body>", "text/html").body.querySelector("template").content;
				generatedByIdiomorph.add(content);
				return content;
			}
		}
		return {
			normalizeElement,
			normalizeParent
		};
	})();
	return {
		morph,
		defaults
	};
})();
function normalizeAttributesForComparison(element) {
	if (!(element instanceof HTMLInputElement && element.type === "file")) {
		if ("value" in element) element.setAttribute("value", element.value);
		else if (element.hasAttribute("value")) element.setAttribute("value", "");
	}
	Array.from(element.children).forEach((child) => {
		normalizeAttributesForComparison(child);
	});
}
const syncAttributes = (fromEl, toEl) => {
	for (let i = 0; i < fromEl.attributes.length; i++) {
		const attr = fromEl.attributes[i];
		toEl.setAttribute(attr.name, attr.value);
	}
};
const setId = (element, id) => {
	if (id === null) element.removeAttribute("id");
	else element.setAttribute("id", id);
};
function executeMorphdom(rootFromElement, rootToElement, modifiedFieldElements, getElementValue, externalMutationTracker, childComponents = []) {
	const externallyChangedIds = /* @__PURE__ */ new Map();
	try {
		for (const [element, originalId] of externalMutationTracker.getOriginalIds()) {
			externallyChangedIds.set(element, element.getAttribute("id"));
			setId(element, originalId);
		}
		for (const { element, id } of childComponents) if (id && element.getAttribute("id") !== id) {
			externallyChangedIds.set(element, element.getAttribute("id"));
			setId(element, id);
		}
		const originalElementIdsToSwapAfter = [];
		const originalElementsToPreserve = /* @__PURE__ */ new Map();
		const markElementAsNeedingPostMorphSwap = (id, replaceWithClone) => {
			const oldElement = originalElementsToPreserve.get(id);
			if (!(oldElement instanceof HTMLElement)) throw new Error(`Original element with id ${id} not found`);
			originalElementIdsToSwapAfter.push(id);
			if (!replaceWithClone) return null;
			const clonedOldElement = cloneHTMLElement(oldElement);
			oldElement.replaceWith(clonedOldElement);
			return clonedOldElement;
		};
		rootToElement.querySelectorAll("[data-live-preserve]").forEach((newElement) => {
			const id = newElement.getAttribute("id");
			if (!id) throw new Error("The data-live-preserve attribute requires an id attribute to be set on the element");
			const oldElement = rootFromElement.querySelector(`#${id}`);
			if (!(oldElement instanceof HTMLElement)) throw new Error(`The element with id "${id}" was not found in the original HTML`);
			newElement.removeAttribute("data-live-preserve");
			originalElementsToPreserve.set(id, oldElement);
			syncAttributes(newElement, oldElement);
		});
		Idiomorph.morph(rootFromElement, rootToElement, { callbacks: {
			beforeNodeMorphed: (fromEl, toEl) => {
				if (!(fromEl instanceof Element) || !(toEl instanceof Element)) return true;
				if (fromEl === rootFromElement) return true;
				const fromId = fromEl.getAttribute("id");
				if (fromId && originalElementsToPreserve.has(fromId)) {
					if (fromId === toEl.getAttribute("id")) return false;
					const clonedFromEl = markElementAsNeedingPostMorphSwap(fromId, true);
					if (!clonedFromEl) throw new Error("missing clone");
					Idiomorph.morph(clonedFromEl, toEl);
					return false;
				}
				if (fromEl instanceof HTMLElement && toEl instanceof HTMLElement) {
					if (typeof fromEl.__x !== "undefined") {
						if (!window.Alpine) throw new Error("Unable to access Alpine.js though the global window.Alpine variable. Please make sure Alpine.js is loaded before Symfony UX LiveComponent.");
						if (typeof window.Alpine.morph !== "function") throw new Error("Unable to access Alpine.js morph function. Please make sure the Alpine.js Morph plugin is installed and loaded, see https://alpinejs.dev/plugins/morph for more information.");
						window.Alpine.morph(fromEl.__x, toEl);
					}
					if (externalMutationTracker.wasElementAdded(fromEl)) {
						const id = fromEl.getAttribute("id");
						if (id && id === toEl.getAttribute("id")) return false;
						toEl.before(document.createComment(""));
						return false;
					}
					if (modifiedFieldElements.includes(fromEl)) setValueOnElement(toEl, getElementValue(fromEl));
					if (fromEl === document.activeElement && fromEl !== document.body && null !== getModelDirectiveFromElement(fromEl, false)) setValueOnElement(toEl, getElementValue(fromEl));
					const elementChanges = externalMutationTracker.getChangedElement(fromEl);
					if (elementChanges) {
						const serverId = toEl.getAttribute("id");
						elementChanges.applyToElement(toEl);
						if (externallyChangedIds.has(fromEl)) setId(toEl, serverId);
					}
					if (fromEl.nodeName.toUpperCase() !== "OPTION" && fromEl.isEqualNode(toEl)) {
						const normalizedFromEl = cloneHTMLElement(fromEl);
						normalizeAttributesForComparison(normalizedFromEl);
						const normalizedToEl = cloneHTMLElement(toEl);
						normalizeAttributesForComparison(normalizedToEl);
						if (normalizedFromEl.isEqualNode(normalizedToEl)) return false;
					}
				}
				if (fromEl.hasAttribute("data-skip-morph") || fromId && fromId !== toEl.getAttribute("id")) {
					fromEl.innerHTML = toEl.innerHTML;
					return true;
				}
				if (fromEl.parentElement?.hasAttribute("data-skip-morph")) return false;
				return !fromEl.hasAttribute("data-live-ignore");
			},
			beforeNodeRemoved(node) {
				if (!(node instanceof HTMLElement)) return true;
				const id = node.getAttribute("id");
				if (id && originalElementsToPreserve.has(id)) {
					markElementAsNeedingPostMorphSwap(id, false);
					return true;
				}
				if (externalMutationTracker.wasElementAdded(node)) return false;
				return !node.hasAttribute("data-live-ignore");
			}
		} });
		originalElementIdsToSwapAfter.forEach((id) => {
			const newElement = rootFromElement.querySelector(`#${id}`);
			const originalElement = originalElementsToPreserve.get(id);
			if (!(newElement instanceof HTMLElement) || !(originalElement instanceof HTMLElement)) throw new Error("Missing elements.");
			newElement.replaceWith(originalElement);
		});
	} finally {
		for (const [element, id] of externallyChangedIds) setId(element, id);
	}
}
var ChangingItemsTracker_default = class {
	changedItems = /* @__PURE__ */ new Map();
	removedItems = /* @__PURE__ */ new Map();
	setItem(itemName, newValue, previousValue) {
		if (this.removedItems.has(itemName)) {
			const removedRecord = this.removedItems.get(itemName);
			this.removedItems.delete(itemName);
			if (removedRecord.original === newValue) return;
		}
		if (this.changedItems.has(itemName)) {
			const originalRecord = this.changedItems.get(itemName);
			if (originalRecord.original === newValue) {
				this.changedItems.delete(itemName);
				return;
			}
			this.changedItems.set(itemName, {
				original: originalRecord.original,
				new: newValue
			});
			return;
		}
		this.changedItems.set(itemName, {
			original: previousValue,
			new: newValue
		});
	}
	removeItem(itemName, currentValue) {
		let trueOriginalValue = currentValue;
		if (this.changedItems.has(itemName)) {
			trueOriginalValue = this.changedItems.get(itemName).original;
			this.changedItems.delete(itemName);
			if (trueOriginalValue === null) return;
		}
		if (!this.removedItems.has(itemName)) this.removedItems.set(itemName, { original: trueOriginalValue });
	}
	getChangedItems() {
		return Array.from(this.changedItems, ([name, { new: value }]) => ({
			name,
			value
		}));
	}
	getRemovedItems() {
		return Array.from(this.removedItems.keys());
	}
	isEmpty() {
		return this.changedItems.size === 0 && this.removedItems.size === 0;
	}
};
var ElementChanges = class {
	addedClasses = /* @__PURE__ */ new Set();
	removedClasses = /* @__PURE__ */ new Set();
	styleChanges = new ChangingItemsTracker_default();
	attributeChanges = new ChangingItemsTracker_default();
	addClass(className) {
		if (!this.removedClasses.delete(className)) this.addedClasses.add(className);
	}
	removeClass(className) {
		if (!this.addedClasses.delete(className)) this.removedClasses.add(className);
	}
	addStyle(styleName, newValue, originalValue) {
		this.styleChanges.setItem(styleName, newValue, originalValue);
	}
	removeStyle(styleName, originalValue) {
		this.styleChanges.removeItem(styleName, originalValue);
	}
	addAttribute(attributeName, newValue, originalValue) {
		this.attributeChanges.setItem(attributeName, newValue, originalValue);
	}
	removeAttribute(attributeName, originalValue) {
		this.attributeChanges.removeItem(attributeName, originalValue);
	}
	getAddedClasses() {
		return [...this.addedClasses];
	}
	getRemovedClasses() {
		return [...this.removedClasses];
	}
	getChangedStyles() {
		return this.styleChanges.getChangedItems();
	}
	getRemovedStyles() {
		return this.styleChanges.getRemovedItems();
	}
	getChangedAttributes() {
		return this.attributeChanges.getChangedItems();
	}
	getRemovedAttributes() {
		return this.attributeChanges.getRemovedItems();
	}
	applyToElement(element) {
		element.classList.add(...this.addedClasses);
		element.classList.remove(...this.removedClasses);
		this.styleChanges.getChangedItems().forEach((change) => {
			if (/!\s*important/i.test(change.value)) element.style.setProperty(change.name, change.value.replace(/!\s*important/i, "").trim(), "important");
			else element.style.setProperty(change.name, change.value);
		});
		this.styleChanges.getRemovedItems().forEach((styleName) => {
			element.style.removeProperty(styleName);
		});
		this.attributeChanges.getChangedItems().forEach((change) => {
			element.setAttribute(change.name, change.value);
		});
		this.attributeChanges.getRemovedItems().forEach((attributeName) => {
			element.removeAttribute(attributeName);
		});
	}
	isEmpty() {
		return this.addedClasses.size === 0 && this.removedClasses.size === 0 && this.styleChanges.isEmpty() && this.attributeChanges.isEmpty();
	}
};
var ExternalMutationTracker_default = class {
	element;
	shouldTrackChangeCallback;
	mutationObserver;
	changedElements = /* @__PURE__ */ new WeakMap();
	changedElementsCount = 0;
	addedElements = [];
	removedElements = [];
	isStarted = false;
	originalIds = /* @__PURE__ */ new Map();
	constructor(element, shouldTrackChangeCallback) {
		this.element = element;
		this.shouldTrackChangeCallback = shouldTrackChangeCallback;
		this.mutationObserver = new MutationObserver(this.onMutations.bind(this));
	}
	start() {
		if (this.isStarted) return;
		this.mutationObserver.observe(this.element, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeOldValue: true
		});
		this.isStarted = true;
	}
	stop() {
		if (this.isStarted) {
			this.mutationObserver.disconnect();
			this.isStarted = false;
		}
	}
	getChangedElement(element) {
		return this.changedElements.has(element) ? this.changedElements.get(element) : null;
	}
	getOriginalIds() {
		for (const element of this.originalIds.keys()) if (!this.element.contains(element)) this.originalIds.delete(element);
		return this.originalIds;
	}
	getAddedElements() {
		return this.addedElements;
	}
	wasElementAdded(element) {
		return this.addedElements.includes(element);
	}
	handlePendingChanges() {
		this.onMutations(this.mutationObserver.takeRecords());
	}
	onMutations(mutations) {
		const handledAttributeMutations = /* @__PURE__ */ new WeakMap();
		for (const mutation of mutations) {
			const element = mutation.target;
			if (!this.shouldTrackChangeCallback(element)) continue;
			if (this.isElementAddedByTranslation(element)) continue;
			let isChangeInAddedElement = false;
			for (const addedElement of this.addedElements) if (addedElement.contains(element)) {
				isChangeInAddedElement = true;
				break;
			}
			if (isChangeInAddedElement) continue;
			switch (mutation.type) {
				case "childList":
					this.handleChildListMutation(mutation);
					break;
				case "attributes":
					if (!handledAttributeMutations.has(element)) handledAttributeMutations.set(element, []);
					if (!handledAttributeMutations.get(element).includes(mutation.attributeName)) {
						this.handleAttributeMutation(mutation);
						handledAttributeMutations.set(element, [...handledAttributeMutations.get(element), mutation.attributeName]);
					}
			}
		}
	}
	handleChildListMutation(mutation) {
		mutation.addedNodes.forEach((node) => {
			if (!(node instanceof Element)) return;
			if (this.removedElements.includes(node)) {
				this.removedElements.splice(this.removedElements.indexOf(node), 1);
				return;
			}
			if (this.isElementAddedByTranslation(node)) return;
			this.addedElements.push(node);
		});
		mutation.removedNodes.forEach((node) => {
			if (!(node instanceof Element)) return;
			if (this.addedElements.includes(node)) {
				this.addedElements.splice(this.addedElements.indexOf(node), 1);
				return;
			}
			this.removedElements.push(node);
		});
	}
	handleAttributeMutation(mutation) {
		const element = mutation.target;
		if (!this.changedElements.has(element)) {
			this.changedElements.set(element, new ElementChanges());
			this.changedElementsCount++;
		}
		const changedElement = this.changedElements.get(element);
		switch (mutation.attributeName) {
			case "class":
				this.handleClassAttributeMutation(mutation, changedElement);
				break;
			case "style":
				this.handleStyleAttributeMutation(mutation, changedElement);
				break;
			default: this.handleGenericAttributeMutation(mutation, changedElement);
		}
		if (changedElement.isEmpty()) {
			this.changedElements.delete(element);
			this.changedElementsCount--;
		}
	}
	handleClassAttributeMutation(mutation, elementChanges) {
		const element = mutation.target;
		const previousValues = (mutation.oldValue || "").match(/(\S+)/gu) || [];
		const newValues = [].slice.call(element.classList);
		const addedValues = newValues.filter((value) => !previousValues.includes(value));
		const removedValues = previousValues.filter((value) => !newValues.includes(value));
		addedValues.forEach((value) => {
			elementChanges.addClass(value);
		});
		removedValues.forEach((value) => {
			elementChanges.removeClass(value);
		});
	}
	handleStyleAttributeMutation(mutation, elementChanges) {
		const element = mutation.target;
		const previousValue = mutation.oldValue || "";
		const previousStyles = this.extractStyles(previousValue);
		const newValue = element.getAttribute("style") || "";
		const newStyles = this.extractStyles(newValue);
		const addedOrChangedStyles = Object.keys(newStyles).filter((key) => previousStyles[key] === void 0 || previousStyles[key] !== newStyles[key]);
		const removedStyles = Object.keys(previousStyles).filter((key) => !newStyles[key]);
		addedOrChangedStyles.forEach((style) => {
			elementChanges.addStyle(style, newStyles[style], previousStyles[style] === void 0 ? null : previousStyles[style]);
		});
		removedStyles.forEach((style) => {
			elementChanges.removeStyle(style, previousStyles[style]);
		});
	}
	handleGenericAttributeMutation(mutation, elementChanges) {
		const attributeName = mutation.attributeName;
		const element = mutation.target;
		if (attributeName === "id") {
			if (!this.originalIds.has(element)) this.originalIds.set(element, mutation.oldValue);
			if (element.getAttribute("id") === this.originalIds.get(element)) this.originalIds.delete(element);
		}
		let oldValue = mutation.oldValue;
		let newValue = element.getAttribute(attributeName);
		if (oldValue === attributeName) oldValue = "";
		if (newValue === attributeName) newValue = "";
		if (!element.hasAttribute(attributeName)) {
			if (oldValue === null) return;
			elementChanges.removeAttribute(attributeName, mutation.oldValue);
			return;
		}
		if (newValue === oldValue) return;
		elementChanges.addAttribute(attributeName, element.getAttribute(attributeName), mutation.oldValue);
	}
	extractStyles(styles) {
		const styleObject = {};
		styles.split(";").forEach((style) => {
			const parts = style.split(":");
			if (parts.length === 1) return;
			const property = parts[0].trim();
			styleObject[property] = parts.slice(1).join(":").trim();
		});
		return styleObject;
	}
	isElementAddedByTranslation(element) {
		return element.tagName === "FONT" && element.getAttribute("style") === "vertical-align: inherit;";
	}
};
var UnsyncedInputsTracker_default = class {
	component;
	modelElementResolver;
	unsyncedInputs;
	elementEventListeners = [{
		event: "input",
		callback: (event) => this.handleInputEvent(event)
	}];
	constructor(component, modelElementResolver) {
		this.component = component;
		this.modelElementResolver = modelElementResolver;
		this.unsyncedInputs = new UnsyncedInputContainer();
	}
	activate() {
		this.elementEventListeners.forEach(({ event, callback }) => {
			this.component.element.addEventListener(event, callback);
		});
	}
	deactivate() {
		this.elementEventListeners.forEach(({ event, callback }) => {
			this.component.element.removeEventListener(event, callback);
		});
	}
	markModelAsSynced(modelName) {
		this.unsyncedInputs.markModelAsSynced(modelName);
	}
	handleInputEvent(event) {
		const target = event.target;
		if (!target) return;
		this.updateModelFromElement(target);
	}
	updateModelFromElement(element) {
		if (!elementBelongsToThisComponent(element, this.component)) return;
		if (!(element instanceof HTMLElement)) throw new Error("Could not update model for non HTMLElement");
		const modelName = this.modelElementResolver.getModelName(element);
		this.unsyncedInputs.add(element, modelName);
	}
	getUnsyncedInputs() {
		return this.unsyncedInputs.allUnsyncedInputs();
	}
	getUnsyncedModels() {
		return Array.from(this.unsyncedInputs.getUnsyncedModelNames());
	}
	resetUnsyncedFields() {
		this.unsyncedInputs.resetUnsyncedFields();
	}
};
var UnsyncedInputContainer = class {
	unsyncedModelFields;
	unsyncedNonModelFields = [];
	unsyncedModelNames = [];
	constructor() {
		this.unsyncedModelFields = /* @__PURE__ */ new Map();
	}
	add(element, modelName = null) {
		if (modelName) {
			this.unsyncedModelFields.set(modelName, element);
			if (!this.unsyncedModelNames.includes(modelName)) this.unsyncedModelNames.push(modelName);
			return;
		}
		this.unsyncedNonModelFields.push(element);
	}
	resetUnsyncedFields() {
		this.unsyncedModelFields.forEach((value, key) => {
			if (!this.unsyncedModelNames.includes(key)) this.unsyncedModelFields.delete(key);
		});
	}
	allUnsyncedInputs() {
		return [...this.unsyncedNonModelFields, ...this.unsyncedModelFields.values()];
	}
	markModelAsSynced(modelName) {
		const index = this.unsyncedModelNames.indexOf(modelName);
		if (index !== -1) this.unsyncedModelNames.splice(index, 1);
	}
	getUnsyncedModelNames() {
		return this.unsyncedModelNames;
	}
};
function getDeepData(data, propertyPath) {
	const { currentLevelData, finalKey } = parseDeepData(data, propertyPath);
	if (currentLevelData === void 0) return;
	return currentLevelData[finalKey];
}
const parseDeepData = (data, propertyPath) => {
	const finalData = JSON.parse(JSON.stringify(data));
	let currentLevelData = finalData;
	const parts = propertyPath.split(".");
	for (let i = 0; i < parts.length - 1; i++) currentLevelData = currentLevelData[parts[i]];
	const finalKey = parts[parts.length - 1];
	return {
		currentLevelData,
		finalData,
		finalKey,
		parts
	};
};
var ValueStore_default = class {
	props = {};
	dirtyProps = {};
	pendingProps = {};
	updatedPropsFromParent = {};
	constructor(props) {
		this.props = props;
	}
	get(name) {
		const normalizedName = normalizeModelName(name);
		if (this.dirtyProps[normalizedName] !== void 0) return this.dirtyProps[normalizedName];
		if (this.pendingProps[normalizedName] !== void 0) return this.pendingProps[normalizedName];
		if (this.props[normalizedName] !== void 0) return this.props[normalizedName];
		return getDeepData(this.props, normalizedName);
	}
	has(name) {
		return this.get(name) !== void 0;
	}
	set(name, value) {
		const normalizedName = normalizeModelName(name);
		if (this.get(normalizedName) === value) return false;
		this.dirtyProps[normalizedName] = value;
		return true;
	}
	getOriginalProps() {
		return { ...this.props };
	}
	getDirtyProps() {
		return { ...this.dirtyProps };
	}
	getUpdatedPropsFromParent() {
		return { ...this.updatedPropsFromParent };
	}
	flushDirtyPropsToPending() {
		this.pendingProps = { ...this.dirtyProps };
		this.dirtyProps = {};
	}
	reinitializeAllProps(props) {
		this.props = props;
		this.updatedPropsFromParent = {};
		this.pendingProps = {};
	}
	pushPendingPropsBackToDirty() {
		this.dirtyProps = {
			...this.pendingProps,
			...this.dirtyProps
		};
		this.pendingProps = {};
	}
	storeNewPropsFromParent(props) {
		let changed = false;
		for (const [key, value] of Object.entries(props)) if (this.get(key) !== value) changed = true;
		if (changed) this.updatedPropsFromParent = props;
		return changed;
	}
};
var Component = class {
	element;
	name;
	listeners;
	backend;
	elementDriver;
	id;
	fingerprint = "";
	valueStore;
	unsyncedInputsTracker;
	hooks;
	defaultDebounce = 150;
	backendRequest = null;
	pendingActions = [];
	pendingFiles = {};
	isRequestPending = false;
	isRemoved = false;
	requestDebounceTimeout = null;
	nextRequestPromise;
	nextRequestPromiseResolve;
	externalMutationTracker;
	constructor(element, name, props, listeners, id, backend, elementDriver) {
		this.element = element;
		this.name = name;
		this.backend = backend;
		this.elementDriver = elementDriver;
		this.id = id;
		this.listeners = /* @__PURE__ */ new Map();
		listeners.forEach((listener) => {
			if (!this.listeners.has(listener.event)) this.listeners.set(listener.event, []);
			this.listeners.get(listener.event)?.push(listener.action);
		});
		this.valueStore = new ValueStore_default(props);
		this.unsyncedInputsTracker = new UnsyncedInputsTracker_default(this, elementDriver);
		this.hooks = new HookManager_default();
		this.resetPromise();
		this.externalMutationTracker = new ExternalMutationTracker_default(this.element, (element) => elementBelongsToThisComponent(element, this));
		this.externalMutationTracker.start();
	}
	getOriginalId() {
		this.externalMutationTracker.handlePendingChanges();
		const originalIds = this.externalMutationTracker.getOriginalIds();
		return originalIds.has(this.element) ? originalIds.get(this.element) : this.element.getAttribute("id");
	}
	addPlugin(plugin) {
		plugin.attachToComponent(this);
	}
	connect() {
		registerComponent(this);
		this.hooks.triggerHook("connect", this);
		this.unsyncedInputsTracker.activate();
		this.externalMutationTracker.start();
	}
	disconnect() {
		unregisterComponent(this);
		this.hooks.triggerHook("disconnect", this);
		this.clearRequestDebounceTimeout();
		this.unsyncedInputsTracker.deactivate();
		this.externalMutationTracker.stop();
	}
	on(hookName, callback) {
		this.hooks.register(hookName, callback);
	}
	off(hookName, callback) {
		this.hooks.unregister(hookName, callback);
	}
	set(model, value, reRender = false, debounce = false) {
		const promise = this.nextRequestPromise;
		const modelName = normalizeModelName(model);
		if (!this.valueStore.has(modelName)) throw new Error(`Invalid model name "${model}".`);
		const isChanged = this.valueStore.set(modelName, value);
		this.hooks.triggerHook("model:set", model, value, this);
		this.unsyncedInputsTracker.markModelAsSynced(modelName);
		if (reRender && isChanged) this.debouncedStartRequest(debounce);
		return promise;
	}
	getData(model) {
		const modelName = normalizeModelName(model);
		if (!this.valueStore.has(modelName)) throw new Error(`Invalid model "${model}".`);
		return this.valueStore.get(modelName);
	}
	action(name, args = {}, debounce = false) {
		const promise = this.nextRequestPromise;
		this.pendingActions.push({
			name,
			args
		});
		this.debouncedStartRequest(debounce);
		return promise;
	}
	files(key, input) {
		this.pendingFiles[key] = input;
	}
	render() {
		const promise = this.nextRequestPromise;
		this.tryStartingRequest();
		return promise;
	}
	getUnsyncedModels() {
		return this.unsyncedInputsTracker.getUnsyncedModels();
	}
	emit(name, data, onlyMatchingComponentsNamed = null) {
		this.performEmit(name, data, false, onlyMatchingComponentsNamed);
	}
	emitUp(name, data, onlyMatchingComponentsNamed = null) {
		this.performEmit(name, data, true, onlyMatchingComponentsNamed);
	}
	emitSelf(name, data) {
		this.doEmit(name, data);
	}
	performEmit(name, data, emitUp, matchingName) {
		findComponents(this, emitUp, matchingName).forEach((component) => {
			component.doEmit(name, data);
		});
	}
	doEmit(name, data) {
		if (!this.listeners.has(name)) return;
		(this.listeners.get(name) || []).forEach((action) => {
			this.action(action, data, 1);
		});
	}
	isTurboEnabled() {
		return typeof Turbo !== "undefined" && !this.element.closest("[data-turbo=\"false\"]");
	}
	removeFromPage() {
		this.isRemoved = true;
		this.disconnect();
		const element = this.element;
		for (const name of element.getAttributeNames()) if (name.startsWith("data-live-") && name.endsWith("-value")) element.removeAttribute(name);
		element.setAttribute("data-live-removing", "");
		requestAnimationFrame(() => {
			const animations = (element.getAnimations?.({ subtree: true }) ?? []).filter((animation) => animation.effect?.getComputedTiming().endTime !== Number.POSITIVE_INFINITY);
			Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
				element.remove();
			});
		});
	}
	tryStartingRequest() {
		if (this.isRemoved) return;
		if (!this.backendRequest) {
			this.performRequest();
			return;
		}
		this.isRequestPending = true;
	}
	performRequest() {
		const thisPromiseResolve = this.nextRequestPromiseResolve;
		this.resetPromise();
		this.unsyncedInputsTracker.resetUnsyncedFields();
		const filesToSend = {};
		for (const [key, value] of Object.entries(this.pendingFiles)) if (value.files) filesToSend[key] = value.files;
		const actionsToSend = this.pendingActions.slice(0, 50);
		const remainingActions = this.pendingActions.slice(50);
		const requestConfig = {
			props: this.valueStore.getOriginalProps(),
			actions: actionsToSend,
			updated: this.valueStore.getDirtyProps(),
			children: {},
			updatedPropsFromParent: this.valueStore.getUpdatedPropsFromParent(),
			files: filesToSend
		};
		this.hooks.triggerHook("request:started", requestConfig);
		this.backendRequest = this.backend.makeRequest(requestConfig.props, requestConfig.actions, requestConfig.updated, requestConfig.children, requestConfig.updatedPropsFromParent, requestConfig.files);
		this.hooks.triggerHook("loading.state:started", this.element, this.backendRequest);
		const historyEntryKey = this.getCurrentHistoryEntryKey();
		const requestUrl = window.location.pathname + window.location.search;
		this.pendingActions = remainingActions;
		this.valueStore.flushDirtyPropsToPending();
		this.isRequestPending = remainingActions.length > 0;
		this.backendRequest.promise.then(async (response) => {
			const backendResponse = new BackendResponse_default(response);
			const headers = backendResponse.response.headers;
			for (const input of Object.values(this.pendingFiles)) input.value = "";
			const html = await backendResponse.getBody();
			if (!headers.get("Content-Type")?.includes("application/vnd.live-component+html") && !headers.get("X-Live-Redirect") && !headers.has("X-Live-Remove")) {
				const controls = { displayError: true };
				this.valueStore.pushPendingPropsBackToDirty();
				this.hooks.triggerHook("loading.state:finished", this.element);
				this.hooks.triggerHook("response:error", backendResponse, controls);
				if (controls.displayError) this.renderError(html);
				this.backendRequest = null;
				thisPromiseResolve(backendResponse);
				return response;
			}
			if (backendResponse.isRemoved()) {
				this.isRemoved = true;
				this.processRerender(html, backendResponse);
				this.backendRequest = null;
				thisPromiseResolve(backendResponse);
				this.removeFromPage();
				return response;
			}
			const liveUrl = backendResponse.getLiveUrl();
			if (liveUrl && this.element.isConnected && this.isOnSamePage(historyEntryKey, requestUrl)) history.replaceState(history.state, "", new URL(liveUrl + window.location.hash, window.location.origin));
			this.processRerender(html, backendResponse);
			this.backendRequest = null;
			thisPromiseResolve(backendResponse);
			if (this.isRequestPending) {
				this.isRequestPending = false;
				this.performRequest();
			}
			return response;
		});
	}
	processRerender(html, backendResponse) {
		const controls = { shouldRender: true };
		this.hooks.triggerHook("render:started", html, backendResponse, controls);
		if (!controls.shouldRender) return;
		if (backendResponse.response.headers.get("Location")) {
			if (this.isTurboEnabled()) Turbo.visit(backendResponse.response.headers.get("Location"));
			else window.location.href = backendResponse.response.headers.get("Location") || "";
			return;
		}
		this.hooks.triggerHook("loading.state:finished", this.element);
		const modifiedModelValues = {};
		Object.keys(this.valueStore.getDirtyProps()).forEach((modelName) => {
			modifiedModelValues[modelName] = this.valueStore.get(modelName);
		});
		let newElement;
		try {
			newElement = htmlToElement(html);
			if (!newElement.matches("[data-controller~=live]")) throw new Error("A live component template must contain a single root controller element.");
		} catch (error) {
			console.error(`There was a problem with the '${this.name}' component HTML returned:`, { id: this.id });
			throw error;
		}
		this.externalMutationTracker.handlePendingChanges();
		this.externalMutationTracker.stop();
		executeMorphdom(this.element, newElement, this.unsyncedInputsTracker.getUnsyncedInputs(), (element) => getValueFromElement(element, this.valueStore), this.externalMutationTracker, findChildren(this));
		this.externalMutationTracker.start();
		const newProps = this.elementDriver.getComponentProps();
		this.valueStore.reinitializeAllProps(newProps);
		const eventsToEmit = this.elementDriver.getEventsToEmit();
		const browserEventsToDispatch = this.elementDriver.getBrowserEventsToDispatch();
		Object.keys(modifiedModelValues).forEach((modelName) => {
			this.valueStore.set(modelName, modifiedModelValues[modelName]);
		});
		eventsToEmit.forEach(({ event, data, target, componentName }) => {
			if (target === "up") {
				this.emitUp(event, data, componentName);
				return;
			}
			if (target === "self") {
				this.emitSelf(event, data);
				return;
			}
			this.emit(event, data, componentName);
		});
		browserEventsToDispatch.forEach(({ event, payload }) => {
			this.element.dispatchEvent(new CustomEvent(event, {
				detail: payload,
				bubbles: true
			}));
		});
		try {
			const downloadUrl = backendResponse.getDownloadUrl();
			const download = backendResponse.getDownload();
			if (downloadUrl) triggerDownload({ url: downloadUrl });
			else if (download) triggerDownload(download);
		} catch (error) {
			console.error("Could not start the download:", error);
		}
		this.hooks.triggerHook("render:finished", this);
	}
	calculateDebounce(debounce) {
		if (debounce === true) return this.defaultDebounce;
		if (debounce === false) return 0;
		return debounce;
	}
	clearRequestDebounceTimeout() {
		if (this.requestDebounceTimeout) {
			clearTimeout(this.requestDebounceTimeout);
			this.requestDebounceTimeout = null;
		}
	}
	debouncedStartRequest(debounce) {
		this.clearRequestDebounceTimeout();
		this.requestDebounceTimeout = window.setTimeout(() => {
			this.render();
		}, this.calculateDebounce(debounce));
	}
	renderError(html) {
		let modal = document.getElementById("live-component-error");
		if (modal) modal.innerHTML = "";
		else {
			modal = document.createElement("div");
			modal.id = "live-component-error";
			modal.style.padding = "50px";
			modal.style.backgroundColor = "rgba(0, 0, 0, .5)";
			modal.style.zIndex = "100000";
			modal.style.position = "fixed";
			modal.style.top = "0px";
			modal.style.bottom = "0px";
			modal.style.left = "0px";
			modal.style.right = "0px";
			modal.style.display = "flex";
			modal.style.flexDirection = "column";
		}
		const iframe = document.createElement("iframe");
		iframe.style.borderRadius = "5px";
		iframe.style.flexGrow = "1";
		modal.appendChild(iframe);
		document.body.prepend(modal);
		document.body.style.overflow = "hidden";
		if (iframe.contentWindow) {
			iframe.contentWindow.document.open();
			iframe.contentWindow.document.write(html);
			iframe.contentWindow.document.close();
		}
		const closeModal = (modal) => {
			if (modal) modal.outerHTML = "";
			document.body.style.overflow = "visible";
		};
		modal.addEventListener("click", () => closeModal(modal));
		modal.setAttribute("tabindex", "0");
		modal.addEventListener("keydown", (e) => {
			if (e.key === "Escape") closeModal(modal);
		});
		modal.focus();
	}
	resetPromise() {
		this.nextRequestPromise = new Promise((resolve) => {
			this.nextRequestPromiseResolve = resolve;
		});
	}
	_updateFromParentProps(props) {
		if (this.valueStore.storeNewPropsFromParent(props)) this.render();
	}
	getCurrentHistoryEntryKey() {
		return window.navigation?.currentEntry?.key ?? null;
	}
	isOnSamePage(historyEntryKey, requestUrl) {
		if (this.getCurrentHistoryEntryKey() === historyEntryKey) return true;
		return window.location.pathname + window.location.search === requestUrl;
	}
};
function proxifyComponent(component) {
	return new Proxy(component, {
		get(component, prop) {
			if (prop in component || typeof prop !== "string") {
				if (typeof component[prop] === "function") {
					const callable = component[prop];
					return (...args) => {
						return callable.apply(component, args);
					};
				}
				return Reflect.get(component, prop);
			}
			if (component.valueStore.has(prop)) return component.getData(prop);
			if ("toJSON" === prop || "then" === prop) return;
			return (args) => {
				return component.action.apply(component, [prop, args]);
			};
		},
		set(target, property, value) {
			if (property in target) {
				target[property] = value;
				return true;
			}
			target.set(property, value);
			return true;
		}
	});
}
function triggerDownload(download) {
	const fromUrl = "url" in download;
	const href = fromUrl ? download.url : URL.createObjectURL(download.blob);
	const link = Object.assign(document.createElement("a"), {
		href,
		download: fromUrl ? "" : download.filename,
		style: "display: none"
	});
	document.body.appendChild(link);
	link.click();
	setTimeout(() => {
		document.body.removeChild(link);
		if (!fromUrl) URL.revokeObjectURL(href);
	}, 75);
}
var StimulusElementDriver = class {
	controller;
	constructor(controller) {
		this.controller = controller;
	}
	getModelName(element) {
		const modelDirective = getModelDirectiveFromElement(element, false);
		if (!modelDirective) return null;
		return modelDirective.action;
	}
	getComponentProps() {
		return this.controller.propsValue;
	}
	getEventsToEmit() {
		return this.controller.eventsToEmitValue;
	}
	getBrowserEventsToDispatch() {
		return this.controller.eventsToDispatchValue;
	}
};
function get_model_binding_default(modelDirective) {
	let shouldRender = true;
	let targetEventName = null;
	let debounce = false;
	let minLength = null;
	let maxLength = null;
	let minValue = null;
	let maxValue = null;
	modelDirective.modifiers.forEach((modifier) => {
		switch (modifier.name) {
			case "on":
				if (!modifier.value) throw new Error(`The "on" modifier in ${modelDirective.getString()} requires a value - e.g. on(change).`);
				if (!["input", "change"].includes(modifier.value)) throw new Error(`The "on" modifier in ${modelDirective.getString()} only accepts the arguments "input" or "change".`);
				targetEventName = modifier.value;
				break;
			case "norender":
				shouldRender = false;
				break;
			case "debounce":
				debounce = modifier.value ? Number.parseInt(modifier.value) : true;
				break;
			case "min_length":
				minLength = modifier.value ? Number.parseInt(modifier.value) : null;
				break;
			case "max_length":
				maxLength = modifier.value ? Number.parseInt(modifier.value) : null;
				break;
			case "min_value":
				minValue = modifier.value ? Number.parseFloat(modifier.value) : null;
				break;
			case "max_value":
				maxValue = modifier.value ? Number.parseFloat(modifier.value) : null;
				break;
			default: throw new Error(`Unknown modifier "${modifier.name}" in data-model="${modelDirective.getString()}".`);
		}
	});
	const [modelName, innerModelName] = modelDirective.action.split(":");
	return {
		modelName,
		innerModelName: innerModelName || null,
		shouldRender,
		debounce,
		targetEventName,
		minLength,
		maxLength,
		minValue,
		maxValue
	};
}
var ChildComponentPlugin_default = class {
	component;
	parentModelBindings = [];
	constructor(component) {
		this.component = component;
		const modelDirectives = getAllModelDirectiveFromElements(this.component.element);
		this.parentModelBindings = modelDirectives.map(get_model_binding_default);
	}
	attachToComponent(component) {
		component.on("request:started", (requestData) => {
			requestData.children = this.getChildrenFingerprints();
		});
		component.on("model:set", (model, value) => {
			this.notifyParentModelChange(model, value);
		});
	}
	getChildrenFingerprints() {
		const fingerprints = {};
		this.getChildren().forEach((child) => {
			if (!child.id) throw new Error("missing id");
			if (this.isInsideSkipMorphElement(child)) return;
			fingerprints[child.id] = {
				fingerprint: child.fingerprint,
				tag: child.element.tagName.toLowerCase()
			};
		});
		return fingerprints;
	}
	notifyParentModelChange(modelName, value) {
		const parentComponent = findParent(this.component);
		if (!parentComponent) return;
		this.parentModelBindings.forEach((modelBinding) => {
			if ((modelBinding.innerModelName || "value") !== modelName) return;
			parentComponent.set(modelBinding.modelName, value, modelBinding.shouldRender, modelBinding.debounce);
		});
	}
	getChildren() {
		return findChildren(this.component);
	}
	isInsideSkipMorphElement(child) {
		const skipMorphElement = child.element.parentElement?.closest("[data-skip-morph]");
		return !!skipMorphElement && skipMorphElement !== this.component.element && this.component.element.contains(skipMorphElement);
	}
};
var LazyPlugin_default = class {
	intersectionObserver = null;
	attachToComponent(component) {
		if ("lazy" !== component.element.attributes.getNamedItem("loading")?.value) return;
		component.on("connect", () => {
			this.getObserver().observe(component.element);
		});
		component.on("disconnect", () => {
			this.intersectionObserver?.unobserve(component.element);
		});
	}
	getObserver() {
		if (!this.intersectionObserver) this.intersectionObserver = new IntersectionObserver((entries, observer) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.dispatchEvent(new CustomEvent("live:appear"));
					observer.unobserve(entry.target);
				}
			});
		});
		return this.intersectionObserver;
	}
};
var LoadingPlugin_default = class {
	attachToComponent(component) {
		component.on("loading.state:started", (element, request) => {
			this.startLoading(component, element, request);
		});
		component.on("loading.state:finished", (element) => {
			this.finishLoading(component, element);
		});
		this.finishLoading(component, component.element);
	}
	startLoading(component, targetElement, backendRequest) {
		this.handleLoadingToggle(component, true, targetElement, backendRequest);
	}
	finishLoading(component, targetElement) {
		this.handleLoadingToggle(component, false, targetElement, null);
	}
	handleLoadingToggle(component, isLoading, targetElement, backendRequest) {
		if (isLoading) targetElement.setAttribute("aria-busy", "true");
		else targetElement.removeAttribute("aria-busy");
		this.getLoadingDirectives(component, targetElement).forEach(({ element, directives }) => {
			if (isLoading) this.addAttributes(element, ["data-live-is-loading"]);
			else this.removeAttributes(element, ["data-live-is-loading"]);
			directives.forEach((directive) => {
				this.handleLoadingDirective(element, isLoading, directive, backendRequest);
			});
		});
	}
	handleLoadingDirective(element, isLoading, directive, backendRequest) {
		const finalAction = parseLoadingAction(directive.action, isLoading);
		const targetedActions = [];
		const targetedModels = [];
		let delay = 0;
		const validModifiers = /* @__PURE__ */ new Map();
		validModifiers.set("delay", (modifier) => {
			if (!isLoading) return;
			delay = modifier.value ? Number.parseInt(modifier.value) : 200;
		});
		validModifiers.set("action", (modifier) => {
			if (!modifier.value) throw new Error(`The "action" in data-loading must have an action name - e.g. action(foo). It's missing for "${directive.getString()}"`);
			targetedActions.push(modifier.value);
		});
		validModifiers.set("model", (modifier) => {
			if (!modifier.value) throw new Error(`The "model" in data-loading must have an action name - e.g. model(foo). It's missing for "${directive.getString()}"`);
			targetedModels.push(modifier.value);
		});
		directive.modifiers.forEach((modifier) => {
			if (validModifiers.has(modifier.name)) {
				(validModifiers.get(modifier.name) ?? (() => {}))(modifier);
				return;
			}
			throw new Error(`Unknown modifier "${modifier.name}" used in data-loading="${directive.getString()}". Available modifiers are: ${Array.from(validModifiers.keys()).join(", ")}.`);
		});
		if (isLoading && targetedActions.length > 0 && backendRequest && !backendRequest.containsOneOfActions(targetedActions)) return;
		if (isLoading && targetedModels.length > 0 && backendRequest && !backendRequest.areAnyModelsUpdated(targetedModels)) return;
		let loadingDirective;
		switch (finalAction) {
			case "show":
				loadingDirective = () => this.showElement(element);
				break;
			case "hide":
				loadingDirective = () => this.hideElement(element);
				break;
			case "addClass":
				loadingDirective = () => this.addClass(element, directive.args);
				break;
			case "removeClass":
				loadingDirective = () => this.removeClass(element, directive.args);
				break;
			case "addAttribute":
				loadingDirective = () => this.addAttributes(element, directive.args);
				break;
			case "removeAttribute":
				loadingDirective = () => this.removeAttributes(element, directive.args);
				break;
			default: throw new Error(`Unknown data-loading action "${finalAction}"`);
		}
		if (delay) {
			window.setTimeout(() => {
				if (backendRequest && !backendRequest.isResolved) loadingDirective();
			}, delay);
			return;
		}
		loadingDirective();
	}
	getLoadingDirectives(component, element) {
		const loadingDirectives = [];
		let matchingElements = Array.from(element.querySelectorAll("[data-loading]"));
		matchingElements = matchingElements.filter((elt) => elementBelongsToThisComponent(elt, component));
		matchingElements = matchingElements.filter((elt) => !elt.closest("[data-live-ignore]"));
		if (element.hasAttribute("data-loading")) matchingElements = [element, ...matchingElements];
		matchingElements.forEach((element) => {
			if (!(element instanceof HTMLElement) && !(element instanceof SVGElement)) throw new Error("Invalid Element Type");
			const directives = parseDirectives(element.dataset.loading || "show");
			loadingDirectives.push({
				element,
				directives
			});
		});
		return loadingDirectives;
	}
	showElement(element) {
		element.style.display = "revert";
	}
	hideElement(element) {
		element.style.display = "none";
	}
	addClass(element, classes) {
		element.classList.add(...combineSpacedArray(classes));
	}
	removeClass(element, classes) {
		element.classList.remove(...combineSpacedArray(classes));
		if (element.classList.length === 0) element.removeAttribute("class");
	}
	addAttributes(element, attributes) {
		attributes.forEach((attribute) => {
			element.setAttribute(attribute, "");
		});
	}
	removeAttributes(element, attributes) {
		attributes.forEach((attribute) => {
			element.removeAttribute(attribute);
		});
	}
};
const parseLoadingAction = (action, isLoading) => {
	switch (action) {
		case "show": return isLoading ? "show" : "hide";
		case "hide": return isLoading ? "hide" : "show";
		case "addClass": return isLoading ? "addClass" : "removeClass";
		case "removeClass": return isLoading ? "removeClass" : "addClass";
		case "addAttribute": return isLoading ? "addAttribute" : "removeAttribute";
		case "removeAttribute": return isLoading ? "removeAttribute" : "addAttribute";
	}
	throw new Error(`Unknown data-loading action "${action}"`);
};
var PageUnloadingPlugin_default = class {
	isConnected = false;
	attachToComponent(component) {
		component.on("render:started", (html, response, controls) => {
			if (!this.isConnected) controls.shouldRender = false;
		});
		component.on("connect", () => {
			this.isConnected = true;
		});
		component.on("disconnect", () => {
			this.isConnected = false;
		});
	}
};
var PollingDirector_default = class {
	component;
	isPollingActive = true;
	polls;
	pollingIntervals = [];
	constructor(component) {
		this.component = component;
	}
	addPoll(actionName, duration) {
		this.polls.push({
			actionName,
			duration
		});
		if (this.isPollingActive) this.initiatePoll(actionName, duration);
	}
	startAllPolling() {
		if (this.isPollingActive) return;
		this.isPollingActive = true;
		this.polls.forEach(({ actionName, duration }) => {
			this.initiatePoll(actionName, duration);
		});
	}
	stopAllPolling() {
		this.isPollingActive = false;
		this.pollingIntervals.forEach((interval) => {
			clearInterval(interval);
		});
	}
	clearPolling() {
		this.stopAllPolling();
		this.polls = [];
		this.startAllPolling();
	}
	initiatePoll(actionName, duration) {
		let callback;
		if (actionName === "$render") callback = () => {
			this.component.render();
		};
		else callback = () => {
			this.component.action(actionName, {}, 0);
		};
		const timer = window.setInterval(() => {
			callback();
		}, duration);
		this.pollingIntervals.push(timer);
	}
};
var PollingPlugin_default = class {
	element;
	pollingDirector;
	attachToComponent(component) {
		this.element = component.element;
		this.pollingDirector = new PollingDirector_default(component);
		this.initializePolling();
		component.on("connect", () => {
			this.pollingDirector.startAllPolling();
		});
		component.on("disconnect", () => {
			this.pollingDirector.stopAllPolling();
		});
		component.on("render:finished", () => {
			this.initializePolling();
		});
	}
	addPoll(actionName, duration) {
		this.pollingDirector.addPoll(actionName, duration);
	}
	clearPolling() {
		this.pollingDirector.clearPolling();
	}
	initializePolling() {
		this.clearPolling();
		if (this.element.dataset.poll === void 0) return;
		const rawPollConfig = this.element.dataset.poll;
		parseDirectives(rawPollConfig || "$render").forEach((directive) => {
			let duration = 2e3;
			directive.modifiers.forEach((modifier) => {
				switch (modifier.name) {
					case "delay":
						if (modifier.value) duration = Number.parseInt(modifier.value);
						break;
					default: console.warn(`Unknown modifier "${modifier.name}" in data-poll "${rawPollConfig}".`);
				}
			});
			this.addPoll(directive.action, duration);
		});
	}
};
var SetValueOntoModelFieldsPlugin_default = class {
	attachToComponent(component) {
		this.synchronizeValueOfModelFields(component);
		component.on("render:finished", () => {
			this.synchronizeValueOfModelFields(component);
		});
	}
	synchronizeValueOfModelFields(component) {
		component.element.querySelectorAll("[data-model]").forEach((element) => {
			if (!(element instanceof HTMLElement)) throw new Error("Invalid element using data-model.");
			if (element instanceof HTMLFormElement) return;
			if (!elementBelongsToThisComponent(element, component)) return;
			const modelDirective = getModelDirectiveFromElement(element);
			if (!modelDirective) return;
			const modelName = modelDirective.action;
			if (component.getUnsyncedModels().includes(modelName)) return;
			if (component.valueStore.has(modelName)) setValueOnElement(element, component.valueStore.get(modelName));
			if (element instanceof HTMLSelectElement && !element.multiple) component.valueStore.set(modelName, getValueFromElement(element, component.valueStore));
		});
	}
};
var ValidatedFieldsPlugin_default = class {
	attachToComponent(component) {
		component.on("model:set", (modelName) => {
			this.handleModelSet(modelName, component.valueStore);
		});
	}
	handleModelSet(modelName, valueStore) {
		if (valueStore.has("validatedFields")) {
			const validatedFields = [...valueStore.get("validatedFields")];
			if (!validatedFields.includes(modelName)) validatedFields.push(modelName);
			valueStore.set("validatedFields", validatedFields);
		}
	}
};
var LiveControllerDefault = class LiveControllerDefault extends Controller {
	static values = {
		name: String,
		url: String,
		props: {
			type: Object,
			default: {}
		},
		propsUpdatedFromParent: {
			type: Object,
			default: {}
		},
		listeners: {
			type: Array,
			default: []
		},
		eventsToEmit: {
			type: Array,
			default: []
		},
		eventsToDispatch: {
			type: Array,
			default: []
		},
		debounce: {
			type: Number,
			default: 150
		},
		fingerprint: {
			type: String,
			default: ""
		},
		requestMethod: {
			type: String,
			default: "post"
		},
		fetchCredentials: {
			type: String,
			default: "same-origin"
		}
	};
	proxiedComponent;
	mutationObserver;
	component;
	pendingActionTriggerModelElement = null;
	elementEventListeners = [{
		event: "input",
		callback: (event) => this.handleInputEvent(event)
	}, {
		event: "change",
		callback: (event) => this.handleChangeEvent(event)
	}];
	pendingFiles = {};
	static backendFactory = (controller) => new Backend_default(controller.urlValue, controller.requestMethodValue, controller.fetchCredentialsValue);
	initialize() {
		this.mutationObserver = new MutationObserver(this.onMutations.bind(this));
		this.createComponent();
	}
	connect() {
		this.connectComponent();
		this.mutationObserver.observe(this.element, { attributes: true });
	}
	disconnect() {
		this.disconnectComponent();
		this.mutationObserver.disconnect();
	}
	update(event) {
		if (event.type === "input" || event.type === "change") throw new Error(`Since LiveComponents 2.3, you no longer need data-action="live#update" on form elements. Found on element: ${getElementAsTagText(event.currentTarget)}`);
		this.updateModelFromElementEvent(event.currentTarget, null);
	}
	action(event) {
		const params = event.params;
		if (!params.action) throw new Error(`No action name provided on element: ${getElementAsTagText(event.currentTarget)}. Did you forget to add the "data-live-action-param" attribute?`);
		const rawAction = params.action;
		const actionArgs = { ...params };
		delete actionArgs.action;
		const directives = parseDirectives(rawAction);
		let debounce = false;
		directives.forEach((directive) => {
			let pendingFiles = {};
			const validModifiers = /* @__PURE__ */ new Map();
			validModifiers.set("stop", () => {
				event.stopPropagation();
			});
			validModifiers.set("self", () => {
				if (event.target !== event.currentTarget) return;
			});
			validModifiers.set("debounce", (modifier) => {
				debounce = modifier.value ? Number.parseInt(modifier.value) : true;
			});
			validModifiers.set("files", (modifier) => {
				if (!modifier.value) pendingFiles = this.pendingFiles;
				else if (this.pendingFiles[modifier.value]) pendingFiles[modifier.value] = this.pendingFiles[modifier.value];
			});
			directive.modifiers.forEach((modifier) => {
				if (validModifiers.has(modifier.name)) {
					(validModifiers.get(modifier.name) ?? (() => {}))(modifier);
					return;
				}
				console.warn(`Unknown modifier ${modifier.name} in action "${rawAction}". Available modifiers are: ${Array.from(validModifiers.keys()).join(", ")}.`);
			});
			for (const [key, input] of Object.entries(pendingFiles)) {
				if (input.files) this.component.files(key, input);
				delete this.pendingFiles[key];
			}
			this.component.action(directive.action, actionArgs, debounce);
			if (getModelDirectiveFromElement(event.currentTarget, false)) this.pendingActionTriggerModelElement = event.currentTarget;
		});
	}
	$render() {
		return this.component.render();
	}
	emit(event) {
		this.getEmitDirectives(event).forEach(({ name, data, nameMatch }) => {
			this.component.emit(name, data, nameMatch);
		});
	}
	emitUp(event) {
		this.getEmitDirectives(event).forEach(({ name, data, nameMatch }) => {
			this.component.emitUp(name, data, nameMatch);
		});
	}
	emitSelf(event) {
		this.getEmitDirectives(event).forEach(({ name, data }) => {
			this.component.emitSelf(name, data);
		});
	}
	$updateModel(model, value, shouldRender = true, debounce = true) {
		return this.component.set(model, value, shouldRender, debounce);
	}
	propsUpdatedFromParentValueChanged() {
		this.component._updateFromParentProps(this.propsUpdatedFromParentValue);
	}
	fingerprintValueChanged() {
		this.component.fingerprint = this.fingerprintValue;
	}
	getEmitDirectives(event) {
		const params = event.params;
		if (!params.event) throw new Error(`No event name provided on element: ${getElementAsTagText(event.currentTarget)}. Did you forget to add the "data-live-event-param" attribute?`);
		const eventInfo = params.event;
		const eventArgs = { ...params };
		delete eventArgs.event;
		const directives = parseDirectives(eventInfo);
		const emits = [];
		directives.forEach((directive) => {
			let nameMatch = null;
			directive.modifiers.forEach((modifier) => {
				switch (modifier.name) {
					case "name":
						nameMatch = modifier.value;
						break;
					default: throw new Error(`Unknown modifier ${modifier.name} in event "${eventInfo}".`);
				}
			});
			emits.push({
				name: directive.action,
				data: eventArgs,
				nameMatch
			});
		});
		return emits;
	}
	createComponent() {
		const id = this.element.getAttribute("id") || null;
		this.component = new Component(this.element, this.nameValue, this.propsValue, this.listenersValue, id, LiveControllerDefault.backendFactory(this), new StimulusElementDriver(this));
		this.proxiedComponent = proxifyComponent(this.component);
		Object.defineProperty(this.element, "__component", {
			value: this.proxiedComponent,
			writable: true
		});
		if (this.hasDebounceValue) this.component.defaultDebounce = this.debounceValue;
		[
			new LoadingPlugin_default(),
			new LazyPlugin_default(),
			new ValidatedFieldsPlugin_default(),
			new PageUnloadingPlugin_default(),
			new PollingPlugin_default(),
			new SetValueOntoModelFieldsPlugin_default(),
			new ChildComponentPlugin_default(this.component)
		].forEach((plugin) => {
			this.component.addPlugin(plugin);
		});
	}
	connectComponent() {
		this.component.connect();
		this.mutationObserver.observe(this.element, { attributes: true });
		this.elementEventListeners.forEach(({ event, callback }) => {
			this.component.element.addEventListener(event, callback);
		});
		this.dispatchEvent("connect");
	}
	disconnectComponent() {
		this.component.disconnect();
		this.elementEventListeners.forEach(({ event, callback }) => {
			this.component.element.removeEventListener(event, callback);
		});
		this.dispatchEvent("disconnect");
	}
	handleInputEvent(event) {
		const target = event.target;
		if (!target) return;
		this.updateModelFromElementEvent(target, "input");
	}
	handleChangeEvent(event) {
		const target = event.target;
		if (!target) return;
		this.updateModelFromElementEvent(target, "change");
	}
	updateModelFromElementEvent(element, eventName) {
		if (!elementBelongsToThisComponent(element, this.component)) return;
		if (!(element instanceof HTMLElement)) throw new Error("Could not update model for non HTMLElement");
		if (element instanceof HTMLInputElement && element.type === "file") {
			const key = element.name;
			if (element.files?.length) this.pendingFiles[key] = element;
			else if (this.pendingFiles[key]) delete this.pendingFiles[key];
		}
		const modelDirective = getModelDirectiveFromElement(element, false);
		if (!modelDirective) return;
		const modelBinding = get_model_binding_default(modelDirective);
		if (!modelBinding.targetEventName) modelBinding.targetEventName = "input";
		if (this.pendingActionTriggerModelElement === element) modelBinding.shouldRender = false;
		if (eventName === "change" && modelBinding.targetEventName === "input") modelBinding.targetEventName = "change";
		if (eventName && modelBinding.targetEventName !== eventName) return;
		if (false === modelBinding.debounce) {
			if (modelBinding.targetEventName === "input") modelBinding.debounce = true;
			else modelBinding.debounce = 0;
		}
		const finalValue = getValueFromElement(element, this.component.valueStore);
		const finalValueIsEmpty = finalValue === "" || finalValue === null || finalValue === void 0;
		if (isTextualInputElement(element) || isTextareaElement(element)) {
			if (!finalValueIsEmpty && modelBinding.minLength !== null && typeof finalValue === "string" && finalValue.length < modelBinding.minLength) return;
			if (!finalValueIsEmpty && modelBinding.maxLength !== null && typeof finalValue === "string" && finalValue.length > modelBinding.maxLength) return;
		}
		if (isNumericalInputElement(element)) {
			if (!finalValueIsEmpty) {
				const numericValue = Number(finalValue);
				if (modelBinding.minValue !== null && numericValue < modelBinding.minValue) return;
				if (modelBinding.maxValue !== null && numericValue > modelBinding.maxValue) return;
			}
		}
		this.component.set(modelBinding.modelName, finalValue, modelBinding.shouldRender, modelBinding.debounce);
	}
	dispatchEvent(name, detail = {}, canBubble = true, cancelable = false) {
		detail.controller = this;
		detail.component = this.proxiedComponent;
		this.dispatch(name, {
			detail,
			prefix: "live",
			cancelable,
			bubbles: canBubble
		});
	}
	onMutations(mutations) {
		mutations.forEach((mutation) => {
			if (mutation.type === "attributes" && mutation.attributeName === "id" && this.component.getOriginalId() !== this.component.id) {
				this.disconnectComponent();
				this.createComponent();
				this.connectComponent();
			}
		});
	}
};
export { Component, LiveControllerDefault as default, getComponent };
